import { createContext, ReactNode, useContext } from 'react';
import { DataProvider } from 'react-admin';
import { ContentTypeProvider, ProgramOptions } from 'utils/react-admin/inputs/ContentTypeInput';
import ContentLangProvider, { LANG } from 'utils/providers/ContentLangProvider';
import ContentNode from '../GeneratedModels/ContentNode';
import { LinkInputProvider, Context as LinkInputContext } from 'utils/react-admin/inputs/LinkInput/LinkInputProvider';
import { BlockTypesProvider } from 'utils/react-admin/inputs/BlocksInput/BlockTypesProvider';
import RaMenuProvider from './RaMenuProvider';

interface Context {
}

const DEFAULTCONTEXT: Context = {
};

const AppConfigContext = createContext<Context>(DEFAULTCONTEXT)

export const useAppConfigContext = () => useContext(AppConfigContext);

interface AppConfigProviderProps {
  children: ReactNode,
}

const RECORD_OPTIONS = [
  { id: 'records', name: 'records', },
];
const PROGRAM_OPTIONS: ProgramOptions = [
  { id: 'home', name: 'home', },
  // { id: 'programs', name: 'programs', },
  { id: 'search', name: 'search', },
  { id: 'membership', name: 'membership', },
  { id: 'membership_registration', name: 'membership_registration', },
  { id: 'volunteer_registration', name: 'volunteer_registration', },
  {
    id: 'donation_plan', name: 'donation_plan',
    blocks: [
      { content_type_data: 'page_title', alias: 'title', },
      { content_type_data: 'section_title', alias: 'sub_title', },
      { content_type_data: 'html', alias: 'intro', },
    ],
  },
  {
    id: 'donation_general', name: 'donation_general',
    blocks: [
      { content_type_data: 'page_title', alias: 'title', },
      { content_type_data: 'section_title', alias: 'sub_title', },
      { content_type_data: 'html', alias: 'intro', },
    ],
  },
];

interface ContentNodeWithSelection extends ContentNode {
  __selection: Array<{ id: number, alias: string, }>;
  _parentIds: Array<number>;
}

const RaMenuSetting = [
  { name: '', label: '', isOpen: false },
]

const LINKS = [
  {
    id: 'content_node',
    label: 'Page Content',
    isTree: true,
    buildUrl: (lang: LANG, linkContext: LinkInputContext, selection: Array<ContentNodeWithSelection>) => {
      console.log('buildUrl', linkContext, selection);

      if (1 === selection.length) {
        //
        let slugs = selection[0]._parentIds.filter(pid => 0 !== pid).map(pid => {
          let cn = linkContext.linkChoices.find((lc: any) => lc.id === pid);
          return (cn) ? (cn as any).tree_node_alias : null;
        });
        slugs.push(selection[0].tree_node_alias);

        //
        let slugRecord = '';
        if (selection[0].__selection && selection[0].__selection.length > 0) {
          slugRecord = `/${selection[0].__selection[0].alias ? selection[0].__selection[0].alias : selection[0].__selection[0].id}`;
        }

        return `/${lang}/${slugs.join('/')}${slugRecord}`;
      } else {
        return '';
      }
    },
    cb: async (dp: DataProvider) => {
      const { data } = await dp.getList<ContentNode>('content_node', { pagination: { page: 1, perPage: 10000 }, sort: { field: 'created', order: 'ASC' }, filter: [] });
      return data.map(cn => {
        const isNested = 'records' === cn.content_type && cn.content_type_data && cn.content_type_data.id && ('activities' === cn.content_type_data.id || 'highlight' === cn.content_type_data.id);
        const nestedCb = isNested ? async (dp: DataProvider) => {
          if (cn.content_type_data && cn.content_type_data.id) {
            const { data } = await dp.getList(cn.content_type_data.id, { pagination: { page: 1, perPage: 10000 }, sort: { field: 'created', order: 'ASC' }, filter: [] });
            return data.map(record => ({ id: record.id, name: `${''} (#${record.id})`, alias: '', isNested: false, }))
          }
          return [];
        } : undefined;
        return ({ ...cn, _isRoot: false, _children: [], _parentIds: [], id: cn.id, name: `${cn.tree_node_alias} (#${cn.id})`, isNested, nestedCb, })
      });
    }
  },
];

const AppConfigProvider = ({ children, ...rest }: AppConfigProviderProps) => {
  return (
    <AppConfigContext.Provider value={{}}>
      <RaMenuProvider menuGroups={RaMenuSetting}>
        <ContentLangProvider>
          <BlockTypesProvider>
            <ContentTypeProvider recordOptions={RECORD_OPTIONS} programOptions={PROGRAM_OPTIONS}>
              <LinkInputProvider links={LINKS}>
                {children}
              </LinkInputProvider>
            </ContentTypeProvider>
          </BlockTypesProvider>
        </ContentLangProvider>
      </RaMenuProvider>
    </AppConfigContext.Provider>
  )
}

export default AppConfigProvider;
